// Slack integration service for sending feedback to Slack channels
import { supabase } from './supabase';

// Interface for Slack message request
export interface SlackMessageRequest {
  text: string;
  username?: string;
  channel?: string;
  icon_emoji?: string;
  attachments?: SlackAttachment[];
}

// Interface for Slack message attachments
export interface SlackAttachment {
  fallback?: string;
  color?: string; // Hex color code, "good", "warning", "danger", etc.
  pretext?: string;
  author_name?: string;
  title?: string;
  text?: string;
  fields?: SlackField[];
  footer?: string;
  ts?: number; // Timestamp
}

// Interface for Slack message fields
export interface SlackField {
  title: string;
  value: string;
  short?: boolean;
}

// Interface for feedback request
export interface FeedbackRequest {
  feedback: string;
  metadata: Record<string, any>;
}

// Interface for Slack API response
export interface SlackResponse {
  success: boolean;
  error?: string;
}

// Class to handle Slack API calls
export class SlackService {
  private webhookUrl: string;

  constructor(webhookUrl?: string) {
    this.webhookUrl = webhookUrl || '';
  }

  // Get a user-friendly error message
  getReadableErrorMessage(error: any): string {
    if (typeof error === 'string') {
      if (error.includes('401') || error.includes('403')) {
        return "Authentication failed. Please check your Slack webhook URL.";
      }
      if (error.includes('429')) {
        return "Too many requests to Slack. Please try again in a moment.";
      }
      if (error.includes('500')) {
        return "Server error. Please try again later.";
      }
    }
    
    return "Failed to send feedback. Please try again.";
  }

  // Send feedback to Slack via Supabase Edge Function
  async sendFeedback(message: string, metadata: Record<string, any> = {}): Promise<SlackResponse> {
    try {
      // Call the Supabase Edge Function directly with the full URL
      const response = await fetch('https://aoczwgqhfvncjzjnxkia.supabase.co/functions/v1/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          feedback: message,
          metadata
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(this.getReadableErrorMessage(`API error: ${response.status} - ${errorText}`));
      }

      const data = await response.json();
      return {
        success: data?.success || false,
        error: data?.error
      };
    } catch (error) {
      console.error("Error sending feedback to Slack:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error occurred"
      };
    }
  }

  // Format a Slack message (kept for reference)
  formatSlackMessage(message: string, metadata: Record<string, any> = {}): SlackMessageRequest {
    // Create a timestamp for the message
    const timestamp = Math.floor(Date.now() / 1000);
    
    // Format the message with rich formatting
    return {
      text: "New User Feedback Received",
      username: "Feedback Bot",
      icon_emoji: ":memo:",
      attachments: [
        {
          color: "#36a64f", // Green color for feedback
          title: "User Feedback",
          text: message,
          fields: Object.entries(metadata).map(([key, value]) => ({
            title: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
            value: String(value),
            short: true
          })),
          footer: "Feedback Widget",
          ts: timestamp
        }
      ]
    };
  }
}

// Export a singleton instance
export const slackService = new SlackService(); 